import { parseAsString, useQueryState } from 'nuqs'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Label } from '@radix-ui/react-label'

interface DataTableSelectorWithQueryProps {
	options: readonly {
		readonly label: string
		readonly value: string
	}[]
	placeholder?: string
	label?: string
	// Query parameter configuration
	queryKey: string
	queryPrefix?: string
	defaultValue?: string
	// Optional value transformation
	onValueChange?: (value: string) => void
	value?: string
}

export function DataTableSelectorWithQuery({
	options,
	placeholder,
	label,
	queryKey,
	defaultValue = '',
	onValueChange,
	value,
}: DataTableSelectorWithQueryProps) {
	const [queryValue, setQueryValue] = useQueryState(
		queryKey,
		parseAsString.withOptions({ shallow: false }).withDefault(defaultValue),
	)

	// Use controlled value if provided, otherwise use query state
	const currentValue = value !== undefined ? value : queryValue

	const allOptions = [{ label: 'ทั้งหมด', value: '' }, ...options]

	const handleSelectedChange = (newValue: string) => {
		// Handle empty string as null for query parameters
		const valueToSet = newValue === '' ? null : newValue

		if (onValueChange) {
			onValueChange(newValue)
		}

		// Only set query state if not controlled by parent
		if (value === undefined) {
			setQueryValue(valueToSet)
		}
	}

	return (
		<div className="flex space-x-2">
			<Select value={currentValue} onValueChange={handleSelectedChange}>
				<SelectTrigger className="w-[180px]">
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{label && <SelectLabel>{label}</SelectLabel>}
						{allOptions.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	)
}

// Convenience component for common use cases
interface DataTableFilterSelectorProps {
	options: readonly {
		readonly label: string
		readonly value: string
	}[]
	filterKey: string
	placeholder?: string
	label?: string
	prefix?: string
	className?: string
}

export function DataTableFilterSelector({
	label,
	options,
	filterKey,
	placeholder,
	prefix,
	className,
}: DataTableFilterSelectorProps) {
	return (
		<div className={className}>
			<Label className="text-sm font-medium">{label}</Label>
			<DataTableSelectorWithQuery
				options={options}
				placeholder={placeholder}
				label={label}
				queryKey={filterKey}
				queryPrefix={prefix}
			/>
		</div>
	)
}
